import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import HeroSection from '../components/ui/HeroSection';
import Section from '../components/ui/Section';
import Button from '../components/ui/Button';
import { PhoneIcon, MailIcon, TimeIcon } from '../utils/IconWrapper';
import { useLanguage } from '../i18n/LanguageContext';
import { sendContactFormEmail } from '../utils/emailService';

// Immagine temporanea da sostituire
const heroImage = 'https://images.unsplash.com/photo-1557426272-fc759fdf7a8d?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=crop&w=1350&q=80';

const ContactWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const ContactInfo = styled.div`
  padding-right: 2rem;
  
  @media (max-width: 992px) {
    padding-right: 0;
  }
`;

const ContactTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 50px;
    height: 2px;
    background-color: var(--accent-primary);
  }
`;

const ContactDescription = styled.p`
  color: var(--text-secondary);
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const ContactItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 2.5rem;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
`;

const IconWrapper = styled.div`
  font-size: 1.5rem;
  color: var(--accent-primary);
  min-width: 1.5rem;
`;

const ContactText = styled.div`
  color: var(--text-secondary);
  
  strong {
    display: block;
    color: var(--text-primary);
    margin-bottom: 0.25rem;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-size: 0.9rem;
  color: var(--text-secondary);
`;

const Input = styled.input`
  padding: 0.8rem;
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: var(--accent-primary);
  }
`;

const Select = styled.select`
  padding: 0.8rem;
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: var(--accent-primary);
  }
  
  option {
    background-color: var(--bg-secondary);
  }
`;

const Textarea = styled.textarea`
  padding: 0.8rem;
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: var(--accent-primary);
  }
`;

const FileInput = styled.div`
  position: relative;
  
  input[type="file"] {
    position: absolute;
    top: 0;
    left: 0;
    opacity: 0;
    width: 100%;
    height: 100%;
    cursor: pointer;
  }
`;

const FileInputLabel = styled.div`
  padding: 0.8rem;
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid var(--border-color);
  color: var(--text-secondary);
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const SubmitButton = styled.div`
  margin-top: 1rem;
`;

const SuccessMessage = styled(motion.div)`
  margin-top: 2rem;
  padding: 1rem;
  border: 1px solid #004744;
  background-color: rgba(0, 71, 68, 0.1);
  color: #004744;
  text-align: center;
`;

const ErrorMessage = styled(motion.div)`
  margin-top: 2rem;
  padding: 1rem;
  border: 1px solid #ef4444;
  background-color: rgba(239, 68, 68, 0.1);
  color: #ef4444;
  text-align: center;
`;

const Contact: React.FC = () => {
  const { t, language } = useLanguage();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    city: '',
    address: '',
    numberOfHousings: '',
    housingType: '',
    serviceType: '',
    comments: '',
    file: null as File | null,
  });
  
  const [fileName, setFileName] = useState(language === 'it' ? 'Scegli file (opzionale)' : 'Choose file (optional)');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFormData(prev => ({ ...prev, file: e.target.files![0] }));
      setFileName(e.target.files[0].name);
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setSubmitError(null);
    setIsSubmitting(true);

    const result = await sendContactFormEmail({
      firstName: formData.firstName,
      lastName: formData.lastName,
      phone: formData.phone,
      email: formData.email,
      city: formData.city,
      address: formData.address,
      numberOfHousings: formData.numberOfHousings,
      housingType: formData.housingType,
      serviceType: formData.serviceType,
      comments: formData.comments,
    });

    setIsSubmitting(false);

    if (!result.success) {
      setSubmitError(
        language === 'it'
          ? "Si è verificato un errore durante l'invio del messaggio. Riprova più tardi o scrivici direttamente a info@j-o-h.com."
          : 'An error occurred while sending your message. Please try again later or email us directly at info@j-o-h.com.'
      );
      return;
    }

    setIsSubmitted(true);

    // Reset form after submission
    setFormData({
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      city: '',
      address: '',
      numberOfHousings: '',
      housingType: '',
      serviceType: '',
      comments: '',
      file: null,
    });
    setFileName(language === 'it' ? 'Scegli file (opzionale)' : 'Choose file (optional)');
    
    // Hide success message after 5 seconds
    setTimeout(() => {
      setIsSubmitted(false);
    }, 5000);
  };
  
  return (
    <>
      <HeroSection
        title={t('contact.title')}
        subtitle={t('contact.subtitle')}
        backgroundImage={heroImage}
        height="60vh"
      />
      
      <Section padding="8rem 0">
        <ContactWrapper>
          <ContactInfo>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <ContactTitle>{language === 'it' ? 'Mettiti in Contatto' : 'Get in Touch'}</ContactTitle>
              <ContactDescription>
                {language === 'it'
                  ? "Che tu stia cercando un alloggio di lusso, sia interessato ai nostri servizi di gestione immobiliare, o abbia altre richieste, il nostro team è pronto ad assisterti."
                  : "Whether you're looking for a luxury accommodation, interested in our property management services, or have any other inquiries, our team is ready to assist you."}
              </ContactDescription>
              
              <ContactItems>
                <ContactItem>
                  <IconWrapper>
                    <PhoneIcon />
                  </IconWrapper>
                  <ContactText>
                    <strong>{t('contact.phone.title')}</strong>
                    +39 353 421 9165
                  </ContactText>
                </ContactItem>
                
                <ContactItem>
                  <IconWrapper>
                    <MailIcon />
                  </IconWrapper>
                  <ContactText>
                    <strong>{t('contact.email.title')}</strong>
                    info@j-o-h.com
                  </ContactText>
                </ContactItem>
                
                <ContactItem>
                  <IconWrapper>
                    <TimeIcon />
                  </IconWrapper>
                  <ContactText>
                    <strong>{t('contact.hours.title')}</strong>
                    H24 - 7/7<br />
                    {language === 'it' ? 'Sempre disponibili per te' : 'Always available for you'}
                  </ContactText>
                </ContactItem>
              </ContactItems>
            </motion.div>
          </ContactInfo>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <ContactTitle>{language === 'it' ? 'Inviaci un Messaggio' : 'Send Us a Message'}</ContactTitle>
            <Form onSubmit={handleSubmit}>
              <FormRow>
                <FormGroup>
                  <Label htmlFor="firstName">{language === 'it' ? 'Nome*' : 'First Name*'}</Label>
                  <Input 
                    type="text" 
                    id="firstName" 
                    name="firstName" 
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="lastName">{language === 'it' ? 'Cognome*' : 'Last Name*'}</Label>
                  <Input 
                    type="text" 
                    id="lastName" 
                    name="lastName" 
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                  />
                </FormGroup>
              </FormRow>
              
              <FormRow>
                <FormGroup>
                  <Label htmlFor="phone">{t('contact.form.phone')}*</Label>
                  <Input 
                    type="tel" 
                    id="phone" 
                    name="phone" 
                    value={formData.phone}
                    onChange={handleChange}
                    required
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="email">{t('contact.form.email')}*</Label>
                  <Input 
                    type="email" 
                    id="email" 
                    name="email" 
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </FormGroup>
              </FormRow>
              
              <FormRow>
                <FormGroup>
                  <Label htmlFor="city">{language === 'it' ? "Città dell'alloggio" : 'City of Housing'}</Label>
                  <Input 
                    type="text" 
                    id="city" 
                    name="city" 
                    value={formData.city}
                    onChange={handleChange}
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="address">{language === 'it' ? 'Indirizzo' : 'Address'}</Label>
                  <Input 
                    type="text" 
                    id="address" 
                    name="address" 
                    value={formData.address}
                    onChange={handleChange}
                  />
                </FormGroup>
              </FormRow>
              
              <FormRow>
                <FormGroup>
                  <Label htmlFor="numberOfHousings">{language === 'it' ? 'Numero di Alloggi' : 'Number of Housings'}</Label>
                  <Input 
                    type="number" 
                    id="numberOfHousings" 
                    name="numberOfHousings" 
                    value={formData.numberOfHousings}
                    onChange={handleChange}
                    min="1"
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="housingType">{language === 'it' ? 'Tipologia di Alloggio' : 'Housing Typology'}</Label>
                  <Select 
                    id="housingType" 
                    name="housingType"
                    value={formData.housingType}
                    onChange={handleChange}
                  >
                    <option value="">{language === 'it' ? 'Seleziona tipologia di alloggio' : 'Select housing type'}</option>
                    <option value="apartment">{language === 'it' ? 'Appartamento' : 'Apartment'}</option>
                    <option value="house">{language === 'it' ? 'Casa' : 'House'}</option>
                    <option value="villa">{language === 'it' ? 'Villa' : 'Villa'}</option>
                    <option value="penthouse">{language === 'it' ? 'Attico' : 'Penthouse'}</option>
                    <option value="other">{language === 'it' ? 'Altro' : 'Other'}</option>
                  </Select>
                </FormGroup>
              </FormRow>
              
              <FormGroup>
                <Label htmlFor="serviceType">{language === 'it' ? 'Servizio Richiesto*' : 'Service Sought*'}</Label>
                <Select 
                  id="serviceType" 
                  name="serviceType"
                  value={formData.serviceType}
                  onChange={handleChange}
                  required
                >
                  <option value="">{language === 'it' ? 'Seleziona tipologia di servizio' : 'Select service type'}</option>
                  <option value="accommodation">{language === 'it' ? 'Prenotazione alloggio' : 'Accommodation Booking'}</option>
                  <option value="property-management">{language === 'it' ? 'Gestione proprietà' : 'Property Management'}</option>
                  <option value="consultation">{language === 'it' ? 'Consulenza investimento immobiliare' : 'Property Investment Consultation'}</option>
                  <option value="other">{language === 'it' ? 'Altri servizi' : 'Other Services'}</option>
                </Select>
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="comments">{language === 'it' ? 'Commenti' : 'Comments'}</Label>
                <Textarea 
                  id="comments" 
                  name="comments"
                  value={formData.comments}
                  onChange={handleChange}
                ></Textarea>
              </FormGroup>
              
              <FormGroup>
                <Label htmlFor="file">{language === 'it' ? 'Allega file' : 'Attach File'}</Label>
                <FileInput>
                  <FileInputLabel>
                    <span>{fileName}</span>
                    <span style={{ color: 'var(--accent-primary)' }}>{language === 'it' ? 'Sfoglia' : 'Browse'}</span>
                  </FileInputLabel>
                  <input 
                    type="file" 
                    id="file" 
                    name="file"
                    onChange={handleFileChange}
                  />
                </FileInput>
              </FormGroup>
              
              <SubmitButton>
                <Button variant="primary" size="large" fullWidth type="submit" disabled={isSubmitting}>
                  {isSubmitting
                    ? (language === 'it' ? 'Invio in corso...' : 'Sending...')
                    : t('contact.form.submit')}
                </Button>
              </SubmitButton>
            </Form>
            
            {isSubmitted && (
              <SuccessMessage
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {language === 'it' ? 'Grazie per il tuo messaggio! Ti risponderemo a breve.' : "Thank you for your message! We'll get back to you shortly."}
              </SuccessMessage>
            )}

            {submitError && (
              <ErrorMessage
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                {submitError}
              </ErrorMessage>
            )}
          </motion.div>
        </ContactWrapper>
      </Section>
    </>
  );
};

export default Contact;

 